package pl.mothdigital.neuronowi.components.layouts

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.keywords.auto
import org.jetbrains.compose.web.dom.*
import pl.mothdigital.neuronowi.common.model.TherapyPageContent
import pl.mothdigital.neuronowi.common.utils.*
import pl.mothdigital.neuronowi.common.utils.fixPolishOrphans
import pl.mothdigital.neuronowi.components.sections.cta.*
import pl.mothdigital.neuronowi.components.text.*
import pl.mothdigital.neuronowi.components.widgets.*
import pl.mothdigital.neuronowi.theme.*

/**
 * Standard layout for therapy pages.
 * Displays therapy information in a consistent, data-driven format.
 *
 * @param content TherapyPageContent containing all page data
 */
@Composable
fun TherapyPageLayout(
    content: TherapyPageContent
) {
    val context = rememberPageContext()

    PageLayout(
        title = content.title,
        description = content.metaDescription ?: content.description,
        ogImage = content.ogImage,
        contentAlignment = Alignment.TopStart
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.Top,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Header Section
            Section(
                attrs = Modifier
                    .fillMaxWidth()
                    .standardSectionPadding()
                    .lightGreenBackground()
                    .toAttrs()
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth().margin { auto },
                    verticalArrangement = Arrangement.spacedBy(1.cssRem)
                ) {
                    AppH1 { Text(content.title) }

                    Row(
                        modifier = Modifier.fillMaxWidth().gap(2.cssRem),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Div(
                            attrs = Modifier
                                .backgroundColor(SiteTheme.green._100)
                                .color(SiteTheme.green._800)
                                .fontSize(4.cssRem)
                                .padding(2.cssRem)
                                .borderRadius(1.cssRem)
                                .toAttrs()
                        ) {
                            content.icon()
                        }

                        AppP(color = SiteTheme.green._800) {
                            Text(content.description.fixPolishOrphans())
                        }
                    }
                }
            }

            // Content Sections
            Section(
                attrs = Modifier
                    .fillMaxWidth()
                    .standardSectionPadding()
                    .toAttrs()
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth().margin { auto }.gap(1.5.cssRem)
                ) {
                    content.sections.forEach { section ->
                        AppH2 { Text(section.title.fixPolishOrphans()) }
                        AppP { Text(section.content.fixPolishOrphans()) }
                    }
                }
            }

            // CTA Section
            CTASection(ctx = context)
        }
    }
}
